/**
 * 图标选择器 — 从 iconRegistry 中选择 Ant Design 图标
 */
import React, { useState, useMemo } from 'react';
import { Popover, Input, Empty } from 'antd';
import { SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getIcon, getRegisteredIconNames } from '@/utils/iconRegistry';

interface IconPickerProps {
  value?: string;
  onChange?: (iconName: string) => void;
}

const ICON_NAMES = getRegisteredIconNames();

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filteredIcons = useMemo(() => {
    if (!search) return ICON_NAMES;
    const kw = search.toLowerCase();
    return ICON_NAMES.filter((name) => name.toLowerCase().includes(kw));
  }, [search]);

  const handleSelect = (iconName: string) => {
    onChange?.(iconName);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.('');
  };

  const SelectedIcon = value ? getIcon(value) : null;

  const content = (
    <div style={{ width: 360 }}>
      <Input
        placeholder="搜索图标..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        size="small"
        style={{ marginBottom: 8 }}
      />
      <div
        style={{
          maxHeight: 240,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 4,
        }}
      >
        {filteredIcons.length === 0 ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <Empty description="无匹配图标" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          filteredIcons.map((name) => {
            const Icon = getIcon(name);
            if (!Icon) return null;
            return (
              <div
                key={name}
                title={name}
                onClick={() => handleSelect(name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 36,
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 18,
                  border: name === value ? '1px solid #1677ff' : '1px solid transparent',
                  background: name === value ? '#e6f4ff' : undefined,
                  transition: 'all .15s',
                }}
                onMouseEnter={(e) => {
                  if (name !== value) e.currentTarget.style.background = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  if (name !== value) e.currentTarget.style.background = '';
                }}
              >
                <Icon />
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      title="选择图标"
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 11px',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          cursor: 'pointer',
          minHeight: 32,
          transition: 'border-color .15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#1677ff')}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#d9d9d9')}
      >
        {SelectedIcon ? (
          <>
            <SelectedIcon />
            <span style={{ flex: 1, fontSize: 13, color: '#666' }}>{value}</span>
            <CloseCircleOutlined
              style={{ color: '#bfbfbf', fontSize: 12 }}
              onClick={handleClear}
            />
          </>
        ) : (
          <span style={{ color: '#bfbfbf' }}>点击选择图标</span>
        )}
      </div>
    </Popover>
  );
};

export default IconPicker;
